import { TRequestHandlerError } from '@studio-toolkit/request-handler';
import { useQuery } from '@tanstack/react-query';

import { useQuantum } from 'hooks';
import { useTokenAndHeaders } from 'hooks/useHeaders';
import { apiUrl, requestHandler } from 'utils';

export interface IChangeLog {
  version: string;
  date: string;
  description: string[];
}

export interface IOrderTypesApiResponse {
  lob: string;
  lineOfBusiness: string;
  configKey: string;
  collateralCode: string;
  collateralVersion: string;
  orderType: string;
  servicesRepresented: string;
  modemType: string | null;
  wifiType: string | null;
  xumoVariant: string | null;
  hasBbs: boolean;
  hasInternet: boolean;
  hasWifi: boolean;
  hasVoice: boolean;
  hasXumo: boolean;
  active: boolean;
  lookupServices: string;
  printed: string;
  updated: string;
  /**
   * Platform string resolved by the backend for PDF generation. Present for
   * special configs (e.g. SONU / ONU fiber) that have no device record and
   * therefore cannot derive a platform from the device fields.
   */
  pickedPlatform?: string;
}

export interface IPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface IFilters {
  lob: string[] | null;
  orderType: string | null;
  device: string | null;
  status: string | null;
  search: string | null;
  availableLobs: string[];
  availableOrderTypes: string[];
  availableDevices: string[];
  availableStatuses: string[];
}

export interface IOrderTypesResponse {
  status: number;
  message: string;
  orderList: IOrderTypesApiResponse[];
  pagination: IPagination;
  filters: IFilters;
}

export interface IOrderTypesResult {
  orderList: IOrderTypesApiResponse[];
  pagination: IPagination;
  filters: IFilters;
}

export interface IFetchAllOrderTypesParams {
  page: number;
  limit: number;
  lob?: string;
  orderType?: string;
  device?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const useFetchAllOrderTypes = ({
  page,
  limit,
  lob,
  orderType,
  device,
  status,
  search,
  sortBy,
  sortOrder,
}: IFetchAllOrderTypesParams) => {
  const { trackApiCall } = useQuantum();

  const { headers } = useTokenAndHeaders();

  return useQuery<IOrderTypesResult, TRequestHandlerError>({
    queryKey: [
      'fetchAllOrderTypes',
      page,
      limit,
      lob,
      orderType,
      device,
      status,
      search,
      sortBy,
      sortOrder,
    ],
    staleTime: 0,
    keepPreviousData: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(page));
      queryParams.append('limit', String(limit));
      if (lob) queryParams.append('lob', lob);
      if (orderType) queryParams.append('orderType', orderType);
      if (device) queryParams.append('device', device);
      if (status) queryParams.append('status', status);
      if (search) queryParams.append('search', search);
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);

      const url = apiUrl({
        endpoint: 'orderTypes/cellular-backup',
        apiEnv: import.meta.env.VITE_APP_ENV,
      });
      const fullUrl = `${url}?${queryParams.toString()}`;

      const response = await requestHandler<IOrderTypesResponse>({
        requestConfig: {
          method: 'GET',
          url: fullUrl,
          headers,
        },
        responseHandlers: {
          200: (res) => res,
        },
        errorHandlers: {
          500: (error) => {
            throw new Error('Failed to fetch order types');
          },
        },
        quantumHandler: trackApiCall,
      });

      const apiRes = response;
      if (
        !apiRes ||
        apiRes.status !== 200 ||
        !Array.isArray(apiRes.orderList)
      ) {
        throw new Error('Order types fetch failed');
      }

      return {
        orderList: apiRes.orderList,
        pagination: apiRes.pagination,
        filters: apiRes.filters,
      };
    },
  });
};

export default useFetchAllOrderTypes;
